import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiClock, FiCalendar, FiBook, FiCheckCircle, FiMic, FiExternalLink } =
  FiIcons;

const scrollToSection = (targetId) => {
  const element = document.getElementById(targetId);
  if (element) {
    const navbarHeight = 80; // Account for fixed navbar plus some padding
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementPosition - navbarHeight,
      behavior: "smooth",
    });
  }
};

const Parcours = () => {
  const parcours = [
    {
      title: "Accompagnement VAE",
      subtitle: "Validation des Acquis de l'Expérience",
      prix: "À partir de 2 800€",
      duree: "Frais de certification inclus",
      icon: FiBook,
      color: "blue",
      url: "https://www.moncompteformation.gouv.fr/espace-prive/html/#/formation/recherche/89279531100018_VAE2/89279531100018_VAE2?contexteFormation=ACTIVITE_PROFESSIONNELLE",
      external: true,
      prerequis: [
        "Expérience significative en formation",
        "Preuves d'activité probantes",
        "Maîtrise du français oral/écrit",
        "Compétences numériques de base",
        "Disponibilité pour la préparation",
      ],
      avantages: [
        "Accompagnement moyen 24h",
        "Examen inclus dans le prix",
        "Préparation complète du dossier",
        "Suivi jusqu'à la certification",
      ],
    },
    {
      title: "Université d'été",
      subtitle: "Formation intensive",
      prix: "À partir de 3 200€",
      duree: "102 heures intensives",
      icon: FiClock,
      color: "green",
      url: "https://www.moncompteformation.gouv.fr/espace-prive/html/#/formation/recherche/82024579300022_FPA/82024579300022_ADF_2026006",
      external: true,
      prerequis: [
        "Niveau de français suffisant",
        "Maîtrise des outils numériques",
        "Expertise métier à transmettre",
        "Projet professionnel défini",
        "Aptitude au travail de groupe",
      ],
      avantages: [
        "Formation accélérée",
        "Groupe restreint dynamique",
        "Méthodes innovantes",
        "Mise en pratique immédiate",
      ],
    },
    {
      title: "Pack Expert",
      subtitle: "VAE + Certification Éloquence",
      prix: "À partir de 4 000€",
      duree: "2 Certifications incluses",
      icon: FiMic,
      color: "orange",
      url: "https://www.moncompteformation.gouv.fr/espace-prive/html/#/formation/recherche/82024579300022_ADF_2024279/82024579300022_P_4",
      external: true,
      prerequis: [
        "Expérience significative en formation",
        "Volonté de maîtriser la prise de parole",
        "Maîtrise du français oral/écrit",
        "Projet d'expertise affirmé",
        "Disponibilité intensive",
      ],
      avantages: [
        "Certification Formateur Adulte",
        "Certification Prise de parole",
        "Double compétence rare",
        "Examens inclus dans le prix",
      ],
    },
    {
      title: "Sessions Longues",
      subtitle: "Septembre et Janvier",
      prix: "À partir de 4 500€",
      duree: "144 heures complètes",
      icon: FiCalendar,
      color: "purple",
      url: "https://www.moncompteformation.gouv.fr/espace-prive/html/#/formation/recherche/82024579300022_FPA/82024579300022_ADF_2026193",
      external: true,
      buttonText: "En savoir plus",
      prerequis: [
        "Niveau de français suffisant",
        "Maîtrise des outils numériques",
        "Expertise métier à transmettre",
        "Projet professionnel défini",
        "Aptitude au travail de groupe",
      ],
      avantages: [
        "Formation exhaustive",
        "Rythme adapté aux salariés",
        "Accompagnement renforcé",
        "Réseau professionnel étendu",
      ],
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: "bg-blue-50",
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
        border: "border-blue-200",
        button: "bg-blue-600 hover:bg-blue-700",
      },
      orange: {
        bg: "bg-orange-50",
        iconBg: "bg-orange-100",
        iconColor: "text-orange-600",
        border: "border-orange-200",
        button: "bg-orange-600 hover:bg-orange-700",
      },
      green: {
        bg: "bg-green-50",
        iconBg: "bg-green-100",
        iconColor: "text-green-600",
        border: "border-green-200",
        button: "bg-green-600 hover:bg-green-700",
      },
      purple: {
        bg: "bg-purple-50",
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
        border: "border-purple-200",
        button: "bg-purple-600 hover:bg-purple-700",
      },
    };
    return colors[color];
  };

  return (
    <section id="parcours" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Nos parcours de formation
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une offre pédagogique modulaire et personnalisable avec quatre
            parcours adaptés à votre situation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {parcours.map((item, index) => {
            const colorClasses = getColorClasses(item.color);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 border-2 ${colorClasses.border} flex flex-col`}
              >
                <div className={`${colorClasses.bg} p-6 text-center`}>
                  <div
                    className={`${colorClasses.iconBg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}
                  >
                    <SafeIcon
                      icon={item.icon}
                      className={`h-8 w-8 ${colorClasses.iconColor}`}
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1 leading-tight h-12 flex items-center justify-center">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-600 mb-4 h-8">
                    {item.subtitle}
                  </p>
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {item.prix}
                  </div>
                  <div className="text-xs font-medium text-gray-500">
                    {item.duree}
                  </div>
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <div className="mb-6 flex-grow">
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm">
                      Prérequis :
                    </h4>
                    <ul className="space-y-2">
                      {item.prerequis.map((req, idx) => (
                        <li
                          key={idx}
                          className="flex items-start space-x-2 text-xs text-gray-600"
                        >
                          <SafeIcon
                            icon={FiCheckCircle}
                            className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0"
                          />
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {item.scrollTarget ? (
                    <button
                      onClick={() => scrollToSection(item.scrollTarget)}
                      className={`block w-full text-center ${colorClasses.button} text-white py-3 rounded-lg font-semibold transition-colors duration-200 text-sm border-none cursor-pointer`}
                    >
                      {item.buttonText || "En savoir plus"}
                    </button>
                  ) : (
                    <a
                      href={item.url}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      className={`block w-full text-center ${colorClasses.button} text-white py-3 rounded-lg font-semibold transition-colors duration-200 text-sm`}
                    >
                      {item.buttonText || "En savoir plus"}
                      {item.external && (
                        <SafeIcon
                          icon={FiExternalLink}
                          className="inline ml-2 h-3 w-3"
                        />
                      )}
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Parcours;
